import { Component, computed, ElementRef, inject, input, output, signal } from "@angular/core";
import { ClickOutsideReason } from "@bitwarden/angular/directives/click-outside.directive";

/**
 * Single-category picker for the transaction form: an inset combobox that expands on click
 * to a scrollable list of raised category cards, the selected category shown as a removable
 * chip, and an inline "Add New Category" create mode. Single-select (emits one name).
 */
@Component({
  selector: "app-category-select",
  templateUrl: "./category-select.component.html",
})
export class CategorySelectComponent {
  private elRef = inject(ElementRef);

  categories = input<string[]>([]);
  selectedName = input<string>("");

  categorySelected = output<string>();
  categoryRemoved = output<void>();
  categoryCreated = output<string>();

  protected searchTerm = signal("");
  protected expanded = signal(false);
  protected creating = signal(false);
  protected newCategoryName = "";

  protected filtered = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    return this.categories().filter((c) => c.toLowerCase().includes(term));
  });

  protected onSearchInput(event: Event) {
    this.searchTerm.set((event.target as HTMLInputElement).value);
    this.expanded.set(true);
  }

  protected onNewNameInput(event: Event) {
    this.newCategoryName = (event.target as HTMLInputElement).value;
  }

  protected expand() {
    if (!this.creating()) {
      this.expanded.set(true);
    }
  }

  protected toggleExpand() {
    if (!this.creating()) {
      this.expanded.update((v) => !v);
    }
  }

  protected onClickOutside(reason: ClickOutsideReason = "outside-click") {
    // ClickOutsideDirective's Escape listener is global to the document, and this component is
    // typically rendered once per row (e.g. one per transaction) — an Escape press meant to
    // dismiss something else on the page must not silently discard state in a row the user
    // isn't even interacting with. Two different risks depending on state, so two different
    // guards:
    //  - expanded() only (browsing, nothing typed): safe to close on any Escape, even without
    //    focus here — there's no data to lose, and this is what lets Escape close a row that
    //    was opened by a click that never moved focus in (e.g. the down-arrow toggle icon).
    //  - creating() (an unsaved typed name is at risk): only close if focus is *also* still
    //    within this instance — otherwise a stray Escape fired after the user tabs away
    //    without explicitly canceling would silently discard the typed name. The cost is that
    //    Escape stays inert — not merely delayed by one press — until focus returns to this
    //    instance (clicking the input again, or dismissing via outside click); that's a far
    //    smaller cost than losing what was typed.
    // A real outside click can't have either problem — ClickOutsideDirective's mousedown/
    // mouseup targeting is already specific to this host — so only Escape is guarded here.
    // Tabbing out ("focus-out") only collapses the list. Unlike an outside click it keeps what was
    // typed: the filter, so moving on without picking still shows the "please select a category"
    // error, and create mode (its input, and the dimmed list the template keeps shown with it)
    // with its unsaved name, for when focus comes back — as for a stray Escape above.
    if (reason === "focus-out") {
      this.expanded.set(false);
      return;
    }
    if (reason === "escape") {
      if (!this.expanded() && !this.creating()) {
        return;
      }
      if (this.creating() && !this.elRef.nativeElement.contains(document.activeElement)) {
        return;
      }
    }
    this.expanded.set(false);
    // The create-input renders whenever `creating()` is true regardless of `expanded`,
    // so tear it down too or it stays stuck open after a click-outside mid-create.
    this.cancelCreate();
    // Clear the filter so the "please select a category" error doesn't linger under a closed combobox.
    this.searchTerm.set("");
  }

  protected selectCategory(name: string) {
    if (this.creating()) {
      return;
    }
    this.categorySelected.emit(name);
    // Clear the filter so the next open shows the full list, not the stale search.
    this.searchTerm.set("");
    this.expanded.set(false);
  }

  protected startCreate() {
    this.creating.set(true);
    this.expanded.set(true);
    this.newCategoryName = "";
  }

  protected cancelCreate() {
    this.creating.set(false);
    this.newCategoryName = "";
  }

  protected confirmCreate() {
    const name = this.newCategoryName.trim();
    if (name) {
      this.categoryCreated.emit(name);
      this.cancelCreate();
      // Clear the filter for parity with selectCategory(), so no stale search term is left behind.
      this.searchTerm.set("");
    }
  }
}
